import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
  
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = null;
      this.predictiveSearchPanel = null;
      this.isOpen = false;
      
      // Check if this predictive search is inside a drawer
      this.isInsideDrawer = false;

      // Bind handlers for proper cleanup
      this.boundClose = () => this.close();
      this.boundCloseWithoutEvent = () => this.closeWithoutEvent();
      this.boundHandleInput = this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this);
    }

    connectedCallback() {
      // Check if inside a search drawer
      this.isInsideDrawer = !!this.closest('search-drawer-component');

      const externalId = this.getAttribute('data-results-container-id');
      this.predictiveSearchPanel = null;
      if (externalId) {
        const hostRoot = document.getElementById(externalId);
        if (hostRoot?.hasAttribute('data-header-predictive-results-host')) {
          this.predictiveSearchPanel = hostRoot;
          this.predictiveSearchResults =
            hostRoot.querySelector('[data-predictive-results-scroll]') || hostRoot;
        } else {
          this.predictiveSearchResults = hostRoot;
        }
      } else {
        this.predictiveSearchResults = this.querySelector('[id^="predictive-search"]');
      }
      
      // Add event listeners
      ThemeBus.addEventListener(EVENTS.cartDrawerOpen, this.boundClose);
      ThemeBus.addEventListener(EVENTS.headerDrawerOpen, this.boundClose);
      ThemeBus.addEventListener(EVENTS.searchDrawerClose, this.boundClose);
      
      // Only listen for predictive search close if NOT inside drawer
      if (!this.isInsideDrawer) {
        ThemeBus.addEventListener(EVENTS.predictiveSearchClose, this.boundClose);
      }
      
      if (this.input) {
        this.input.addEventListener('input', this.boundHandleInput);
      }
    }

    disconnectedCallback() {
      // Remove event listeners
      ThemeBus.removeEventListener(EVENTS.cartDrawerOpen, this.boundClose);
      ThemeBus.removeEventListener(EVENTS.headerDrawerOpen, this.boundClose);
      ThemeBus.removeEventListener(EVENTS.searchDrawerClose, this.boundClose);
      
      // Only remove predictive search close listener if NOT inside drawer
      if (!this.isInsideDrawer) {
        ThemeBus.removeEventListener(EVENTS.predictiveSearchClose, this.boundClose);
      }
      
      if (this.input) {
        this.input.removeEventListener('input', this.boundHandleInput);
      }
    }
  
    onChange() {
      const searchTerm = this.input.value.trim();
  
      if (!searchTerm.length) {
        this.close();
        return;
      }
  
      this.getSearchResults(searchTerm);
    }
  
    getSearchResults(searchTerm) {
      fetch(`/search/suggest?q=${searchTerm}&resources[type]=product,collection,article&section_id=predictive-search`)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
  
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          
          if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = resultsMarkup;
          
          // Dispatch gridUpdated event to trigger lazy loading (centralized in lazy-image.js)
          ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));
          
          this.open();
          } else {
            console.warn('[PredictiveSearch] No predictive search results container found');
          }
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }
  
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      if (this.predictiveSearchPanel) {
        this.predictiveSearchPanel.classList.remove('header-predictive-closed');
        this.predictiveSearchPanel.classList.add('header-predictive-open');
        this.predictiveSearchPanel.setAttribute('aria-hidden', 'false');
      } else if (this.predictiveSearchResults) {
        // Drawer mount uses Tailwind `flex flex-1 min-h-0 overflow-y-auto` on #predictive-search-drawer.
        // `display: block` overrides `display: flex` and breaks height constraints so results never scroll.
        if (this.isInsideDrawer) {
          this.predictiveSearchResults.style.removeProperty('display');
        } else {
          this.predictiveSearchResults.style.display = 'block';
        }
      }

      // Only show header overlay if NOT inside a drawer
      if (!this.isInsideDrawer) {
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
      }
    }
  
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this.predictiveSearchPanel) {
        this.predictiveSearchPanel.classList.remove('header-predictive-open');
        this.predictiveSearchPanel.classList.add('header-predictive-closed');
        this.predictiveSearchPanel.setAttribute('aria-hidden', 'true');
      } else if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'none';
      }

      // Only hide header overlay if NOT inside a drawer
      if (!this.isInsideDrawer) {
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
      }

      // Clear the input when closing
      if (this.input) {
        this.input.value = '';
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    closeWithoutEvent() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this.predictiveSearchPanel) {
        this.predictiveSearchPanel.classList.remove('header-predictive-open');
        this.predictiveSearchPanel.classList.add('header-predictive-closed');
        this.predictiveSearchPanel.setAttribute('aria-hidden', 'true');
      } else if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'none';
      }

      // Only hide header overlay if NOT inside a drawer
      if (!this.isInsideDrawer) {
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
      }
    }
  
    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);

// ========================================
// Header hover search: keep bar open when query has text (data-has-value on group)
// ========================================

const HEADER_SEARCH_EXPAND = '[data-header-search-expand]';

function syncHeaderSearchExpandGroup(input) {
  const group = input.closest(HEADER_SEARCH_EXPAND);
  if (!group) return;
  if (input.value.trim().length > 0) {
    group.setAttribute('data-has-value', '');
  } else {
    group.removeAttribute('data-has-value');
  }
}

const headerSearchExpandInitialized = new WeakSet();

function initHeaderSearchExpandState() {
  document.querySelectorAll(`${HEADER_SEARCH_EXPAND} .header-search-q`).forEach((input) => {
    if (headerSearchExpandInitialized.has(input)) return;
    headerSearchExpandInitialized.add(input);
    const sync = () => syncHeaderSearchExpandGroup(input);
    input.addEventListener('input', sync);
    sync();
  });
}

// ========================================
// Search Input Clear Button Handler
// ========================================

// Store listener references for cleanup
const searchClearButtonListeners = new WeakMap();

function initSearchClearButtons() {
  const clearButtons = document.querySelectorAll('[data-search-clear]');
  
  clearButtons.forEach(button => {
    const inputId = button.getAttribute('data-search-clear');
    const input = document.getElementById(inputId);
    
    if (!input) return;
    
    // Skip if already initialized
    if (searchClearButtonListeners.has(button)) return;
    
    // Show/hide clear button based on input value
    const toggleClearButton = () => {
      if (input.value.trim().length > 0) {
        button.classList.remove('opacity-0', 'invisible');
        button.classList.add('opacity-100', 'visible');
      } else {
        button.classList.remove('opacity-100', 'visible');
        button.classList.add('opacity-0', 'invisible');
      }
    };
    
    // Handle clear button click
    const handleClearClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      input.value = '';
      input.focus();
      toggleClearButton();
      syncHeaderSearchExpandGroup(input);
      
      // Close predictive search if open
      const predictiveSearch = input.closest('predictive-search');
      if (predictiveSearch && typeof predictiveSearch.close === 'function') {
        predictiveSearch.close();
      }
    };
    
    // Add listeners
    button.addEventListener('click', handleClearClick);
    input.addEventListener('input', toggleClearButton);
    
    // Store references for potential cleanup
    searchClearButtonListeners.set(button, {
      handleClearClick,
      toggleClearButton,
      input
    });
    
    // Initial state
    toggleClearButton();
  });
}

// Cleanup function (call to remove all search clear button listeners)
function cleanupSearchClearButtons() {
  const clearButtons = document.querySelectorAll('[data-search-clear]');
  
  clearButtons.forEach(button => {
    const listeners = searchClearButtonListeners.get(button);
    if (listeners) {
      button.removeEventListener('click', listeners.handleClearClick);
      listeners.input.removeEventListener('input', listeners.toggleClearButton);
      searchClearButtonListeners.delete(button);
    }
  });
}

// Store handler references for module-level cleanup
const handleDOMContentLoadedForClear = () => {
  initHeaderSearchExpandState();
  initSearchClearButtons();
};

const handleModalOpenForClear = () => {
  setTimeout(() => {
    initHeaderSearchExpandState();
    initSearchClearButtons();
  }, 100);
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', handleDOMContentLoadedForClear);
} else {
  initHeaderSearchExpandState();
  initSearchClearButtons();
}

// Reinitialize after modal opens (in case modal wasn't in DOM initially)
ThemeBus.addEventListener(EVENTS.modalOpen, handleModalOpenForClear);

// Export cleanup function
export function cleanupSearchModule() {
  cleanupSearchClearButtons();
  ThemeBus.removeEventListener(EVENTS.modalOpen, handleModalOpenForClear);
  // Note: DOMContentLoaded listener is one-time only, no need to remove
}